import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip as RTooltip,
  PieChart, Pie, Cell, Legend,
} from "recharts";
import { TrendingUp, Building2, FolderKanban, Target, DollarSign } from "lucide-react";
import api from "@/lib/api";
import { useCountry } from "@/lib/CountryContext";
import HotLeads from "@/components/HotLeads";

const STAGE_COLORS = {
  qualification: "#94A3B8",
  proposal: "#2563EB",
  negotiation: "#D97706",
  won: "#059669",
  lost: "#DC2626",
};

const SECTOR_COLORS = ["#004B87", "#2563EB", "#059669", "#D97706", "#7C3AED", "#DC2626", "#0891B2", "#65A30D"];

const COUNTRY_CENTER = {
  UAE: [24.7, 54.5, 7],
  KSA: [24.5, 45.0, 5],
  QAT: [25.3, 51.2, 8],
  OMN: [21.5, 56.0, 6],
  BHR: [26.0, 50.5, 10],
  KWT: [29.3, 47.8, 8],
};

// Custom industrial marker
const makeIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative;width:18px;height:18px;">
      <div style="position:absolute;inset:0;background:${color};border:2px solid white;box-shadow:0 0 0 1px ${color}33;"></div>
      <div class="pulse-dot" style="position:absolute;inset:-4px;border-radius:0;"></div>
    </div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

function fmtMoney(v) {
  if (!v) return "$0";
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

function KPI({ label, value, hint, icon: Icon, accent, testid }) {
  return (
    <div className="section-card p-5 relative overflow-hidden" data-testid={testid}>
      <div
        className="absolute top-0 right-0 w-1 h-full"
        style={{ background: accent || "#004B87" }}
      />
      <div className="flex items-start justify-between mb-3">
        <div className="label-eyebrow">{label}</div>
        <Icon size={16} className="text-gray-400" strokeWidth={1.5} />
      </div>
      <div className="font-display text-3xl font-bold tracking-tight">{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { country } = useCountry();
  const [k, setK] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/kpis", { params: { country } }),
      api.get("/companies", { params: { country } }),
      api.get("/projects", { params: { country } }),
    ]).then(([a, b, c]) => {
      setK(a.data);
      setCompanies(b.data);
      setProjects(c.data);
    });
  }, [country]);

  if (!k) return <div className="text-gray-400">Loading…</div>;

  const stageData = Object.entries(k.by_stage || {}).map(([s, v]) => ({
    stage: s, value: v,
  }));
  const sectorData = Object.entries(k.by_sector || {}).map(([s, v]) => ({
    name: s.replace(/_/g, " "), value: v,
  }));
  const emirateData = Object.entries(k.by_emirate || {}).map(([s, v]) => ({
    emirate: s, count: v,
  }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <div className="label-eyebrow">Region · {country}</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
            Business Development Console
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            Live snapshot of Sodimate's pipeline — cement, water, waste-to-energy &amp; desalination.
          </p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPI
          label="Pipeline Value"
          value={fmtMoney(k.pipeline_value)}
          hint={`Weighted: ${fmtMoney(k.weighted_pipeline)}`}
          icon={DollarSign}
          accent="#004B87"
          testid="kpi-pipeline-value"
        />
        <KPI
          label="Won Revenue"
          value={fmtMoney(k.won_value)}
          hint="Closed deals"
          icon={TrendingUp}
          accent="#059669"
          testid="kpi-won-value"
        />
        <KPI
          label="Companies"
          value={k.companies}
          hint="Prospects to clients"
          icon={Building2}
          testid="kpi-companies"
        />
        <KPI
          label="Active Projects"
          value={k.active_projects}
          hint={`of ${k.projects} total`}
          icon={FolderKanban}
          accent="#D97706"
          testid="kpi-active-projects"
        />
        <KPI
          label="Conversion"
          value={`${k.conversion_rate}%`}
          hint={`${k.opportunities} opportunities`}
          icon={Target}
          accent="#7C3AED"
          testid="kpi-conversion"
        />
      </div>

      {/* Map + Pipeline by stage */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 section-card overflow-hidden" data-testid="uae-map-card">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="label-eyebrow">Map</div>
              <div className="font-display text-lg font-semibold mt-1">
                {country} — Companies &amp; Projects
              </div>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 bg-[#004B87] inline-block" /> Companies
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 bg-[#D97706] inline-block" /> Projects
              </span>
            </div>
          </div>
          <div style={{ height: 420 }}>
            <MapContainer
              key={country}
              center={[COUNTRY_CENTER[country]?.[0] ?? 24.7, COUNTRY_CENTER[country]?.[1] ?? 54.5]}
              zoom={COUNTRY_CENTER[country]?.[2] ?? 7}
              scrollWheelZoom={false}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution=""
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />
              {companies.filter((c) => c.lat && c.lng).map((c) => (
                <Marker key={c.id} position={[c.lat, c.lng]} icon={makeIcon("#004B87")}>
                  <Popup>
                    <div className="font-bold text-sm">{c.name}</div>
                    <div className="text-xs text-gray-500 uppercase">{c.sector}</div>
                    <div className="text-xs">{c.emirate} · {c.status}</div>
                  </Popup>
                </Marker>
              ))}
              {projects.filter((p) => p.lat && p.lng).map((p) => (
                <Marker key={p.id} position={[p.lat, p.lng]} icon={makeIcon("#D97706")}>
                  <Popup>
                    <div className="font-bold text-sm">{p.name}</div>
                    <div className="text-xs text-gray-500 uppercase">{p.sector} · {p.status}</div>
                    <div className="text-xs">{fmtMoney(p.estimated_value_usd)}</div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        <div className="section-card p-5" data-testid="pipeline-by-stage">
          <div className="label-eyebrow">Pipeline</div>
          <div className="font-display text-lg font-semibold mt-1 mb-4">By Stage</div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={stageData} layout="vertical" margin={{ left: 20 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#E5E7EB" />
                <XAxis type="number" tickFormatter={fmtMoney} stroke="#6B7280" fontSize={11} />
                <YAxis dataKey="stage" type="category" stroke="#6B7280" fontSize={11} width={90} />
                <RTooltip
                  formatter={(v) => fmtMoney(v)}
                  contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 2 }}
                />
                <Bar dataKey="value" radius={0}>
                  {stageData.map((d, i) => (
                    <Cell key={i} fill={STAGE_COLORS[d.stage] || "#004B87"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Sectors & Emirates & Hot Leads */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="section-card p-5" data-testid="sector-breakdown">
          <div className="label-eyebrow">Companies</div>
          <div className="font-display text-lg font-semibold mt-1 mb-4">By Sector</div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={sectorData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                >
                  {sectorData.map((_, i) => (
                    <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />
                  ))}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <RTooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 2 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="section-card p-5" data-testid="emirate-breakdown">
          <div className="label-eyebrow">Projects</div>
          <div className="font-display text-lg font-semibold mt-1 mb-4">By Emirate</div>
          <div style={{ height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={emirateData}>
                <CartesianGrid strokeDasharray="2 4" stroke="#E5E7EB" />
                <XAxis dataKey="emirate" stroke="#6B7280" fontSize={11} />
                <YAxis stroke="#6B7280" fontSize={11} allowDecimals={false} />
                <RTooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 2 }} />
                <Bar dataKey="count" fill="#004B87" radius={0} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <HotLeads country={country} />
      </div>
    </div>
  );
}
